import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, Users, FileText, Share2, BookOpen, X, Loader2,
} from "lucide-react";

/**
 * Global header search. Live-debounced cross-entity search (Care Seekers, Notes,
 * Referrals, Resources). Dropdown groups results by type. Keyboard: Esc closes,
 * Enter on the first result navigates.
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q: term, limit: 5 } });
        setResults(data);
      } catch {
        setResults({ care_seekers: [], notes: [], referrals: [], resources: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click / Esc / Cmd-K to focus
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const go = (path) => {
    setOpen(false);
    setQ("");
    setResults(null);
    navigate(path);
  };

  const hasResults = results && results.total > 0;
  const noResults = results && results.total === 0 && q.trim().length >= 2 && !loading;

  return (
    <div ref={wrapRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
        <input
          ref={inputRef}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Search care seekers, notes, referrals…"
          data-testid="global-search-input"
          className="w-full pl-9 pr-9 py-2 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-sm focus:outline-none focus:bg-white focus:border-[#7E937D] transition-colors"
        />
        {q && (
          <button
            onClick={() => { setQ(""); setResults(null); inputRef.current?.focus(); }}
            data-testid="global-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-[#1E3A8A] hover:text-[#0F172A]"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#1E3A8A] animate-spin" />
        )}
      </div>

      {open && (q.length >= 2 || hasResults) && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white border border-[#E5E5E0] rounded-2xl shadow-xl max-h-[70vh] overflow-y-auto z-50"
          data-testid="global-search-results"
        >
          {noResults && (
            <div className="p-6 text-center">
              <div className="text-sm font-medium text-[#0F172A]">No matches for "{q}"</div>
              <div className="text-xs text-[#1E3A8A] mt-1">Try a partial name, phone number, or organization.</div>
            </div>
          )}

          {hasResults && results.care_seekers.length > 0 && (
            <ResultGroup label="Care Seekers" icon={Users}>
              {results.care_seekers.map((c) => (
                <ResultRow
                  key={c.id}
                  testId={`search-cs-${c.id}`}
                  onClick={() => go(`/care-seekers/${c.id}`)}
                  primary={`${c.preferred_name || c.first_name} ${c.last_name}`}
                  secondary={[c.phone, c.county && `${c.county} County`, c.status].filter(Boolean).join(" · ")}
                />
              ))}
            </ResultGroup>
          )}

          {hasResults && results.notes.length > 0 && (
            <ResultGroup label="Wellness notes" icon={FileText}>
              {results.notes.map((n) => (
                <ResultRow
                  key={n.id}
                  testId={`search-note-${n.id}`}
                  onClick={() => go(n.care_seeker_id ? `/care-seekers/${n.care_seeker_id}` : "/notes")}
                  primary={n.care_seeker_label || "Wellness note"}
                  secondary={n.snippet || (n.interaction_type || "").replace(/_/g, " ")}
                />
              ))}
            </ResultGroup>
          )}

          {hasResults && results.referrals.length > 0 && (
            <ResultGroup label="Referrals" icon={Share2}>
              {results.referrals.map((r) => (
                <ResultRow
                  key={r.id}
                  testId={`search-ref-${r.id}`}
                  onClick={() => go(r.care_seeker_id ? `/care-seekers/${r.care_seeker_id}` : "/referrals")}
                  primary={r.organization || "Referral"}
                  secondary={[(r.category || "").replace(/_/g, " "), r.status, r.care_seeker_label].filter(Boolean).join(" · ")}
                />
              ))}
            </ResultGroup>
          )}

          {hasResults && results.resources.length > 0 && (
            <ResultGroup label="Resources" icon={BookOpen}>
              {results.resources.map((r) => (
                <ResultRow
                  key={r.id}
                  testId={`search-res-${r.id}`}
                  onClick={() => go("/resources")}
                  primary={r.name}
                  secondary={[(r.category || "").replace(/_/g, " "), r.county, r.phone].filter(Boolean).join(" · ")}
                />
              ))}
            </ResultGroup>
          )}

          {hasResults && (
            <div className="border-t border-[#F0EFEB] px-4 py-2 text-[10px] text-[#1E3A8A] flex justify-between items-center">
              <span>{results.total} match{results.total === 1 ? "" : "es"}</span>
              <span className="hidden sm:inline">⌘K or Ctrl-K to focus search</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="px-4 pt-3 pb-1.5 flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#F9F8F6]">
        <Icon className="w-3 h-3" strokeWidth={1.75} />
        {label}
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function ResultRow({ primary, secondary, onClick, testId }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="w-full text-left px-4 py-2.5 hover:bg-[#F9F8F6] transition-colors border-b border-[#F0EFEB] last:border-0"
      >
        <div className="text-sm font-medium text-[#0F172A] truncate">{primary}</div>
        {secondary && <div className="text-xs text-[#1E3A8A] truncate">{secondary}</div>}
      </button>
    </li>
  );
}
